import React from 'react';
import { AccommodationCombined } from '../../Accommodations/Accommodation.types';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useTranslation } from '@shared/core';
import { Box, BoxProps, Divider, Flex, JoyKitThemeProvider, TooltipV2 } from '@withjoy/joykit';
import { Skeleton, SkeletonGroup } from '@shared/components/Skeleton';
import { Info } from '@withjoy/joykit/icons';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { TravelCurrencyCode } from '@graphql/generated';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useHotelCurrencyCode } from '@shared/hooks/useHotelCurrencyCode';
import { usePricingByJoyPlaceIdFragment } from '@shared/hooks/usePricingByPropertyIds';
import { ExpediaIcon } from '@assets/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

type JoyPlacePricingProps = { accommodation: AccommodationCombined } & BoxProps;

// Joy places display pricing differently from room blocks
export const JoyPlacePricing = (props: JoyPlacePricingProps) => {
  const [accommodationsPriceAndRedirectEnabled, loading] = useIsFeatureEnabled('accommodationsPriceAndRedirectEnabled');
  if (loading) return null;
  return accommodationsPriceAndRedirectEnabled ? <PricingByAccommodationPlace {...props} /> : <PricingByJoyPlace {...props} />;
};

const PricingByJoyPlace = (props: JoyPlacePricingProps) => {
  const { price, strikeOutPrice, loading } = usePricingByJoyPlaceIdFragment(props.accommodation.joyPlaceId);
  if (!props.accommodation.joyPlaceId) return null;
  return <JoyPlacePricingRoot {...props} price={price} strikeOutPrice={strikeOutPrice} loading={loading} />;
};

const PricingByAccommodationPlace = (props: JoyPlacePricingProps) => {
  const { price, strikeOutPrice, loading } = useTravelPlacesPricingAndBookingDetailsFragment(props.accommodation.id, 'accommodationPlace');
  return <JoyPlacePricingRoot {...props} price={price} strikeOutPrice={strikeOutPrice} loading={loading} />;
};

type JoyPlacePricingRootProps = JoyPlacePricingProps & { price: Maybe<string>; strikeOutPrice: Maybe<string>; loading: Maybe<boolean> };

const JoyPlacePricingRoot = ({ price, strikeOutPrice, loading, accommodation, ...boxProps }: JoyPlacePricingRootProps) => {
  const { eventInfo } = useEventInfo();
  const eventDisplayName = eventInfo?.eventDisplayName;
  const { t: accommodationsTrans } = useTranslation('guestSiteCustom');
  const accTrans = accommodationsTrans('accommodations');
  return (
    ((price || loading) && (
      <JoyKitThemeProvider>
        <Box {...boxProps}>
          <Divider marginBottom={5} />
          <SkeletonGroup
            isReady={!loading}
            placeholder={
              <Flex gap={2} justifyContent="space-between" alignItems={'flex-start'}>
                <Skeleton
                  placeholder={
                    <Box width={'8rem'} typographyVariant={'body1'}>
                      &nbsp;
                    </Box>
                  }
                />

                <Box>
                  <Box display={'flex'} typographyVariant={'body1'} alignItems={'center'} justifyContent={'flex-end'} marginBottom={1}>
                    <Skeleton
                      placeholder={
                        <Box width={'3.5rem'} typographyVariant={'body1'}>
                          &nbsp;
                        </Box>
                      }
                    />
                  </Box>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
                    <Skeleton
                      placeholder={
                        <Box width={'3.5rem'} typographyVariant={'hed2'}>
                          &nbsp;
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              </Flex>
            }
          >
            {price && (
              <>
                <Box color={'black'}>
                  <Box display={'flex'} typographyVariant={'body1'} alignItems={'center'} justifyContent={'flex-end'} marginBottom={1}>
                    {accommodationsTrans('accommodations', 'exclusiveRate')()}
                    {eventDisplayName ? (
                      <Box marginLeft={2}>
                        <TooltipV2 content={accTrans.exclusiveRateInfo({ name: eventDisplayName })} onMouseEnterDelay={500} placement={'topEnd'}>
                          <Info size={'sm'} color={'mono10'} title={undefined} />
                        </TooltipV2>
                      </Box>
                    ) : null}
                  </Box>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} marginTop={3}>
                    <Box typographyVariant={'body1'} color={'mono14'} marginRight={2}>
                      {accTrans.roomsStartingFrom()}
                    </Box>
                    <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
                      <Box typographyVariant={'hed2'} justifyContent={'flex-end'} display={'flex'}>
                        <IsoCurrencyIndicator />
                        {accTrans.pricePerNight({ price })}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {strikeOutPrice ? <StrikeOutPrice strikeOutPrice={strikeOutPrice} /> : null}
              </>
            )}
          </SkeletonGroup>
        </Box>
      </JoyKitThemeProvider>
    )) ||
    null
  );
};

const IsoCurrencyIndicator = () => {
  const { code } = useHotelCurrencyCode();
  const { value: localeBasedCurrencyOnOrOff } = useRemoteFeatureFlag('localeBasedHotelCurrency');
  const queryParamHelper = useQueryParamHelper();
  const showCurrencyCode = queryParamHelper.getValueString('showCurrencyCode') === 'true';

  return showCurrencyCode ? <Box marginRight={2}>{localeBasedCurrencyOnOrOff === 'on' ? code : TravelCurrencyCode.USD}</Box> : null;
};

const StrikeOutPrice = ({ strikeOutPrice }: { strikeOutPrice: string }) => {
  const { t: websiteEditorTrans } = useTranslation('websiteEditor');
  const { value, loading } = useRemoteFeatureFlag('accommodationsPricingStrikeoutExperiment');

  const expediaCompareTo = websiteEditorTrans('expediaCompareTo')({
    ExpediaCompareTo: <ExpediaIcon />
  });
  return loading || value !== 'treatment' ? null : (
    <Box
      typographyVariant={'body3'}
      height={pxToRem(48)}
      alignItems={'center'}
      color={'inputPlaceholderDisabled'}
      display={'flex'}
      borderTop={'solid 1px'}
      borderColor={'mono5'}
      justifyContent={'space-between'}
      marginTop={3}
    >
      <Box typographyVariant="body1" color="#191E3B" alignItems="center" gap={1} marginRight={2}>
        {expediaCompareTo}
      </Box>
      <Box textDecoration={'line-through'} color={'mono14'}>
        {strikeOutPrice}
      </Box>
    </Box>
  );
};
